const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DAY = 24 * 60 * 60 * 1000;

/**
 * Email signup pop-up. Opens once after a delay unless the visitor already signed up or dismissed
 * it (kept in localStorage, with a cookie as backup, so it survives visits). Signup posts the
 * Shopify customer form in the background; on success the discount code is revealed with a copy
 * button. If Shopify asks for a captcha, the form is submitted normally and the code is shown
 * when the page comes back.
 */
class WearthSignupPopup extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const dialog = this.dialog;
    if (!dialog) return;

    for (const button of this.querySelectorAll('[data-close]')) {
      button.addEventListener('click', () => dialog.close(), { signal });
    }
    // Any click outside the tag (backdrop or background image) closes it
    dialog.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && !event.target.closest('.wearth-popup__tag')) dialog.close();
      },
      { signal }
    );
    dialog.addEventListener('close', this.#handleClose, { signal });
    this.form?.addEventListener('submit', this.#handleSubmit, { signal });
    this.querySelector('[data-email]')?.addEventListener('input', () => this.#setError(''), { signal });
    this.querySelector('[data-copy]')?.addEventListener('click', this.#copyCode, { signal });

    if (this.hasAttribute('data-design-mode')) {
      this.#setupEditor(signal);
      return;
    }

    // Back from a normal (captcha) submission: Shopify adds ?customer_posted=true#<form id>
    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true' && window.location.hash === `#${this.dataset.formId}`) {
      this.#saveState('signed-up');
      this.open();
      this.#showCode();
      return;
    }

    if (this.#shouldShow()) {
      this.#timer = window.setTimeout(() => this.open(), Number(this.dataset.delay || 0) * 1000);
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    window.clearTimeout(this.#timer);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('[data-dialog]');
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    return /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
  }

  get storageKey() {
    return `wearth-popup:${this.dataset.version || 'default'}`;
  }

  open() {
    const dialog = this.dialog;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }

  #shouldShow() {
    if (this.hasAttribute('data-blocked')) return false;

    const devices = this.dataset.devices;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    if ((devices === 'desktop' && mobile) || (devices === 'mobile' && !mobile)) return false;

    const state = this.#readState();
    if (!state) return true;
    if (state.status === 'signed-up') return false;

    // Dismissed: never again (0 days) or after the chosen number of days
    const days = Number(this.dataset.dismissDays || 0);
    return days > 0 && Date.now() - state.at > days * DAY;
  }

  /** @param {AbortSignal} signal */
  #setupEditor(signal) {
    const sectionId = this.dataset.sectionId;
    if (this.hasAttribute('data-preview')) this.open();
    document.addEventListener(
      'shopify:section:select',
      (event) => /** @type {CustomEvent} */ (event).detail?.sectionId === sectionId && this.open(),
      { signal }
    );
    document.addEventListener(
      'shopify:section:deselect',
      (event) => /** @type {CustomEvent} */ (event).detail?.sectionId === sectionId && this.dialog?.close(),
      { signal }
    );
  }

  #handleClose = () => {
    if (this.hasAttribute('data-design-mode')) return;
    if (this.#readState()?.status !== 'signed-up') this.#saveState('dismissed');
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    const form = this.form;
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-email]'));
    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-submit]'));
    if (!form || !input) return;

    input.value = input.value.trim();
    if (!EMAIL_PATTERN.test(input.value)) {
      this.#setError(this.dataset.errorInvalid || '');
      input.focus();
      return;
    }

    if (this.hasAttribute('data-design-mode')) {
      this.#showCode();
      return;
    }

    if (submit) submit.disabled = true;
    try {
      // Same encoding as a normal form post
      const body = new URLSearchParams(/** @type {any} */ (new FormData(form)));
      const response = await fetch(form.action, { method: 'POST', body, credentials: 'same-origin' });
      const url = new URL(response.url);

      if (url.pathname.startsWith('/challenge')) {
        // Shopify wants a captcha: submit normally, the code shows when the page returns
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      if (response.ok && url.searchParams.get('customer_posted') === 'true') {
        this.#saveState('signed-up');
        this.#showCode();
        return;
      }

      // Re-rendered page with this form's error message
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const message = html.getElementById(this.dataset.formId || '')?.querySelector('[data-error]')?.textContent?.trim();
      if (message && /already|taken/i.test(message)) {
        // Already on the list: still give them the code
        this.#saveState('signed-up');
        this.#showCode();
        return;
      }
      this.#setError(message || this.dataset.errorGeneric || '');
    } catch {
      this.#setError(this.dataset.errorGeneric || '');
    } finally {
      if (submit) submit.disabled = false;
    }
  };

  #showCode() {
    this.querySelector('[data-step="signup"]')?.setAttribute('hidden', '');
    this.querySelector('[data-step="code"]')?.removeAttribute('hidden');
    /** @type {HTMLElement | null} */ (this.querySelector('[data-copy]'))?.focus();
  }

  #copyCode = async () => {
    const code = this.querySelector('[data-code]')?.textContent?.trim() || '';
    const status = /** @type {HTMLElement | null} */ (this.querySelector('[data-copied]'));
    let copied = false;

    try {
      await navigator.clipboard.writeText(code);
      copied = true;
    } catch {
      // Older browsers / blocked clipboard: copy through a temporary selection
      const field = document.createElement('textarea');
      field.value = code;
      field.setAttribute('readonly', '');
      field.style.position = 'fixed';
      field.style.opacity = '0';
      this.dialog?.append(field);
      field.select();
      copied = document.execCommand('copy');
      field.remove();
    }

    if (status) {
      status.textContent = '';
      // Re-set so screen readers announce it again on repeat copies
      window.requestAnimationFrame(() => {
        status.textContent = copied ? status.dataset.text || '' : code;
      });
    }
  };

  /** @param {string} message */
  #setError(message) {
    const error = this.querySelector('[data-error]');
    const input = this.querySelector('[data-email]');
    if (error) error.textContent = message;
    if (message) input?.setAttribute('aria-invalid', 'true');
    else input?.removeAttribute('aria-invalid');
  }

  /** @returns {{ status: string, at: number } | null} */
  #readState() {
    try {
      const stored = window.localStorage.getItem(this.storageKey);
      if (stored) return JSON.parse(stored);
    } catch {
      // Storage blocked: fall back to the cookie
    }
    const match = document.cookie.match(new RegExp(`(?:^|; )${encodeURIComponent(this.storageKey)}=([^;]*)`));
    if (!match?.[1]) return null;
    const [status = '', at = '0'] = decodeURIComponent(match[1]).split('|');
    return { status, at: Number(at) };
  }

  /** @param {'signed-up' | 'dismissed'} status */
  #saveState(status) {
    const at = Date.now();
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify({ status, at }));
    } catch {
      // Storage blocked: the cookie below still remembers it
    }
    document.cookie = `${encodeURIComponent(this.storageKey)}=${encodeURIComponent(`${status}|${at}`)}; max-age=${
      365 * 24 * 60 * 60
    }; path=/; SameSite=Lax`;
  }
}

if (!customElements.get('wearth-signup-popup')) {
  customElements.define('wearth-signup-popup', WearthSignupPopup);
}
